import './header.css';
import logo from '../assets/images/dashboard/lepton-icono.png';
import { useState, useEffect, useCallback, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import MenuItem from '../components/header/menuItem/menuItem';
import { DEFAULT_USER_CONSTANTS } from '../App';

const apiDashboardUrl = 'https://dashboard-api.lepton-seguridad.com';
// const apiDashboardUrl = 'http://localhost:3103';

const Header = ({ onLogout, userHash, sectionOrder, setSectionOrder, devices, setDevices, trackersGroups, isMobileDevicesVisible, setIsMobileDevicesVisible, userConstants, updateUserConstants, setUserConstants }) => {
    const [userName, setUserName] = useState('');
    const [userID, setUserID] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false); // Estado para mostrar u ocultar menú en móviles
    const [showMenuText, setShowMenuText] = useState(false);
    const menuRef = useRef(null); // Referencia para el menú
    const lastScrollPosition = useRef(0);

    const handleLogout = useCallback(() => {
        document.cookie = 'session_key=; expires=; path=/;';
        onLogout();
    }, [onLogout]);

    useEffect(() => {
        if (userHash) {
            const fetchUserInfo = async () => {
                try {
                    const response = await fetch('https://pro.lepton-seguridad.com/api-v2/user/get_info', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json, text/plain, */*'
                        },
                        body: JSON.stringify({ hash: userHash }),
                    });

                    if (!response.ok) {
                        handleLogout();
                        return;
                    }

                    const data = await response.json();
                    const userData = data.user_info;

                    try {
                        // 1. Verificamos si existe en BD
                        const res = await fetch(`${apiDashboardUrl}/users?hash=${userHash}`, {
                            method: 'GET',
                            headers: { 'Content-Type': 'application/json' },
                        });

                        if (res.ok) {
                            const dbUser = await res.json();
                            setUserConstants(dbUser.data.userConstants);
                        } else if (res.status === 404) {
                            // 2. No existe, lo creamos
                            const createRes = await fetch(`${apiDashboardUrl}/users`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ hash: userHash })
                            });

                            if (!createRes.ok) {
                                throw new Error('Error al crear el nuevo usuario en base de datos');
                            }

                            const newUser = await createRes.json();
                        } else {
                            throw new Error('Error desconocido al consultar el usuario');
                        }
                    } catch (err) {
                        setUserConstants({
                            ...DEFAULT_USER_CONSTANTS,
                            userName: userData.title
                        });
                        console.error('Error al sincronizar datos del usuario con la base de datos:', err);
                    }


                    // Siempre actualizamos nombre y ID en estado local
                    setUserName(userData.title);
                    setUserID(userData.id);

                } catch (error) {
                    console.error('Error al conectar con el servidor:', error);
                }
            };

            fetchUserInfo();
        }
    }, [userHash]);


    useEffect(() => {
        if (devices.length > 0) {
            setIsLoading(false);
        }
    }, [devices]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                toggleMenu();
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    const [menuItems, setMenuItems] = useState(sectionOrder);
    const [draggedIndex, setDraggedIndex] = useState(null); // Estado para el índice arrastrado

    useEffect(() => {
        setMenuItems(sectionOrder);
    }, [sectionOrder]);

    // Reordena las secciones en el menú y en la página principal
    const moveMenuItem = (dragIndex, hoverIndex) => {
        const updatedItems = [...menuItems];
        const [draggedItem] = updatedItems.splice(dragIndex, 1);
        updatedItems.splice(hoverIndex, 0, draggedItem);

        setMenuItems(updatedItems);
        setSectionOrder(updatedItems);
        updateUserConstants({ orderOfSections: updatedItems.map(item => item.id) });
    };

    const toggleMenuText = () => {
        setShowMenuText(!showMenuText);
    };

    const mobileMenuRef = useRef(null);

    const toggleMobileMenu = (event) => {
        // event.stopPropagation();
        setIsMobileMenuVisible(!isMobileMenuVisible);
        if (isMobileDevicesVisible) {
            setIsMobileDevicesVisible(false);
        }
    };

    useEffect(() => {
        // Bloquear/desbloquear scroll en el body
        if (isMobileMenuVisible) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }

        // Limpiar al desmontar
        return () => {
            document.body.classList.remove('no-scroll');
        };
    }, [isMobileMenuVisible]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedOutsideMenu = mobileMenuRef.current && !mobileMenuRef.current.contains(event.target);
            const clickedMenuToggle = event.target.closest('.mobile_menu_button');
            const clickedDevicesBtn = event.target.closest('.mobile_devices_button'); // ⬅️ nuevo
            if (clickedOutsideMenu && !clickedMenuToggle && !clickedDevicesBtn) {
                setIsMobileMenuVisible(false);
            }
        };

        if (isMobileMenuVisible) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isMobileMenuVisible]);


    const toggleMobileDevices = (event) => {
        event?.stopPropagation?.();        // ⬅️ importante
        setIsMobileDevicesVisible(v => !v);
        if (isMobileMenuVisible) setIsMobileMenuVisible(false);
    };


    // Lógica de desplazamiento para mostrar/ocultar el header
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollPosition = window.scrollY;

            if (
                currentScrollPosition > lastScrollPosition.current &&
                currentScrollPosition > 50 &&
                isHeaderVisible
            ) {
                setIsHeaderVisible(false);
            } else if (
                currentScrollPosition < lastScrollPosition.current &&
                !isHeaderVisible
            ) {
                setIsHeaderVisible(true);
            }

            lastScrollPosition.current = currentScrollPosition;
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isHeaderVisible]);

    return (
        <header className={`header_bg_container ${isHeaderVisible ? '' : 'hidden'}`}>
            <div className='header_container' style={{ position: 'relative' }}>

                <button
                    className='mobile_devices_button'
                    onClick={toggleMobileDevices}
                >
                    {/* <span>Dispositivos</span> */}
                    <span className='material-symbols-outlined'>
                        {isMobileDevicesVisible ? 'close' : 'menu'}
                    </span>

                </button>

                <div className='header_logo_container'>
                    <a href='https://www.lepton-seguridad.com/' target='_blank' rel='noreferrer' className='header_logo'>
                        <img src={logo} alt='Leptón logo'></img>
                    </a>
                </div>
                <nav className={`header_tabs_container ${showMenuText ? 'text_visible' : ''}`}>
                    <DndProvider backend={HTML5Backend}>
                        {menuItems.map((item, index) => (
                            <MenuItem
                                key={item.id}
                                item={item}
                                index={index}
                                draggedIndex={draggedIndex} // Pasamos draggedIndex al componente MenuItem
                                setDraggedIndex={setDraggedIndex} // Pasamos setDraggedIndex al componente MenuItem
                                moveMenuItem={moveMenuItem}
                                onCloseMenu={toggleMenu}
                            />
                        ))}
                    </DndProvider>
                    <button className='menu_text_btn' onClick={toggleMenuText}>
                        <span className='material-symbols-outlined'>{showMenuText ? 'visibility' : 'visibility_off'}</span>
                    </button>
                </nav>
                <div className='header_user_container' style={{ display: 'none' }}>
                    <button className='header_logout_btn' onClick={handleLogout}>
                        <span className='material-symbols-outlined'>logout</span>
                    </button>
                </div>

                <button
                    className='mobile_menu_button'
                    onClick={toggleMobileMenu}
                >
                    {/* <span>Navegación</span> */}
                    <span className='material-symbols-outlined'>
                        {isMobileMenuVisible ? 'close' : 'menu'}
                    </span>
                </button>
                <nav ref={mobileMenuRef} className={`mobile_menu_container ${isMobileMenuVisible ? 'visible' : 'hidden'
                    }`}>
                    <DndProvider backend={HTML5Backend}>
                        {menuItems.map((item, index) => (
                            <MenuItem
                                key={item.id}
                                item={item}
                                index={index}
                                draggedIndex={draggedIndex} // Pasamos draggedIndex al componente MenuItem
                                setDraggedIndex={setDraggedIndex} // Pasamos setDraggedIndex al componente MenuItem
                                moveMenuItem={moveMenuItem}
                                onCloseMenu={toggleMobileMenu}
                            />
                        ))}
                    </DndProvider>
                    <div className='logout_btn_container' style={{ display: 'none' }}>
                        <button className='logout_btn' onClick={handleLogout}>
                            <span className='material-symbols-outlined'>logout</span>
                            <span>Cerrar Sesión</span>
                        </button>
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;
